import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo, LogoMark } from "@/components/lunelle/Logo";
import { InviteCard } from "@/components/lunelle/InviteCard";
import { confirmEmail } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

export default function ConfirmPage() {
  usePageMeta("Confirm your email | Lunelle", "Confirm your email to secure your founding-member spot on the Lunelle waitlist.");
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [state, setState] = useState(token ? "loading" : "missing");

  useEffect(() => {
    if (!token) return undefined;
    confirmEmail(token)
      .then((d) => { setData(d); setState("ready"); })
      .catch(() => setState("missing"));
    return undefined;
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col" data-testid="confirm-page">
      <header className="mx-auto w-full max-w-7xl px-5 py-6 sm:px-8 lg:px-12"><Link to="/" data-testid="confirm-home-link"><Logo /></Link></header>
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-5 pb-24">
        {state === "loading" && <p className="text-[#6B5A52]">Confirming your email…</p>}
        {state === "missing" && (
          <>
            <h1 className="font-serif text-5xl font-light text-[#2C221E]" data-testid="confirm-title">Link not recognised.</h1>
            <p className="mt-5 text-lg text-[#5C4A42]">This confirmation link is incomplete or your details were removed. You can join again from the homepage.</p>
          </>
        )}
        {state === "ready" && (
          <>
            <motion.span initial={{ scale: 0.6, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 180, damping: 16 }}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-[#9E4429]">
              <LogoMark className="h-9 w-9" color="#FBF5EE" />
            </motion.span>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-[#9E4429]">{data?.just_confirmed ? "Email confirmed" : "Already confirmed"}</p>
            <h1 className="mt-3 font-serif text-5xl font-light leading-tight text-[#2C221E] sm:text-6xl" data-testid="confirm-title">
              Your spot is secured. You're #{(data?.position ?? 0).toLocaleString()}
            </h1>
            <p className="mt-4 text-[#5C4A42]">{data?.just_confirmed ? "Thank you for confirming. If a friend invited you, they've just moved up the line." : "You've already confirmed this email — nothing more to do."}</p>
            <div className="mt-8 w-full">
              <InviteCard code={data?.referral_code} referrals={data?.referrals ?? 0} pending={data?.pending_referrals ?? 0} spots={data?.spots_per_friend ?? 5} prefix="confirm" />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
